/**
 * The room — the first screen of a shift.
 *
 * A grid of tables, not a drag-and-drop canvas. What a waiter needs from it is
 * one question answered at a glance: which tables need me?
 *
 * So colour is urgency, not category. Green through amber to red is how long a
 * party has been sitting; violet is the one state that is not about time at all
 * — they have asked for the bill — and it is the brand violet precisely because
 * it must not read as a point on that ramp. Every tile also says its state in
 * words: a dim dining room and a colour-blind waiter both take the hue away,
 * and the answer has to survive that.
 *
 * The violet branch cannot be reached today. `check_requested` and `paying` are
 * both in the floor's state machine and neither has anything that publishes
 * the event that would enter them, so `tableTone` never returns `accent` in
 * practice. That is a missing feature rather than dead styling — the states
 * and the colour are ready for whatever asks for the bill first.
 *
 * Tapping a table opens its actions as a sheet rather than revealing a panel
 * further down the page. On a tablet the panel was below the fold: you tapped a
 * table and nothing appeared to happen.
 *
 * Positioned layout comes with the plan editor in Professional. A grid is
 * honest for now, and better than a half-built canvas nobody can arrange.
 */

import { useState, type ReactNode } from 'react';
import {
  describeTableState,
  formatSeatedFor,
  tableTone,
  useCheckRequested,
  useClearTable,
  useCreateFloorPlan,
  useCreateSection,
  useCreateTable,
  useLogins,
  usePermission,
  useSections,
  useSetTableSection,
  useUpdateSection,
  useEntitlements,
  isExpected,
  useBookingDay,
  useFloor,
  useMergeTables,
  useOpenOrder,
  useReleaseTable,
  useRemoveFloorPlan,
  useRemoveTable,
  useUpdateTable,
  useSeatTable,
  useUnmergeTables,
  type FloorTable,
  type Section,
} from '@restaurant/api-client';
import {
  ActionError,
  Button,
  Empty,
  ErrorNotice,
  Field,
  Input,
  Loading,
  Segmented,
  Sheet,
  UpgradePrompt,
} from '@restaurant/ui';

import { FoodUpTag } from './food-up';
import { describeFoodUp, useFoodUp } from './readiness';
import { ArrangeRoom, RoomMap } from './room-layout';
import { isArranged, useLayOutRoom } from './room-layout-state';
import './bookings.css';

/** How far ahead a booking is said on its table in the room. */
const BOOKED_AHEAD_MS = 3 * 60 * 60 * 1000;

const GUEST_PRESETS = ['1', '2', '3', '4', '5', '6', '8'] as const;

export function FloorPlan({
  locationId,
  onOpenOrder,
}: {
  locationId: string | undefined;
  onOpenOrder: (orderId: string) => void;
}) {
  const floor = useFloor(locationId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [planId, setPlanId] = useState<string | null>(null);
  const [addingRoom, setAddingRoom] = useState(false);
  const [arranging, setArranging] = useState(false);
  const [laidOut, setLaidOut] = useState<Map<string, { x: number; y: number }> | null>(null);
  // A list first: every table in order, readable at a glance. The map is there
  // for a room somebody has arranged, a tap away.
  const [view, setView] = useState<'room' | 'tables'>('tables');
  const layOut = useLayOutRoom();
  const mayManage = usePermission('floor.plan.manage');
  // Before the early returns below, because a hook cannot be called
  // conditionally — it asks about nothing until the room has loaded.
  const foodUp = useFoodUp((floor.data ?? []).flatMap((p) => p.tables.map((t) => t.order_id)));
  // The next booking on each table, so a host does not sit a walk-in at the
  // table the eight o'clock is booked on. Enterprise; asked for nothing else.
  const entitlements = useEntitlements();
  const bookings = useBookingDay(locationId, undefined, {
    enabled: entitlements.has('floor.reservations'),
  });
  const nextBooking = new Map<string, string>();
  const now = Date.now();
  for (const booking of bookings.data?.bookings ?? []) {
    const at = new Date(booking.starts_at).getTime();
    if (!isExpected(booking) || at < now - 30 * 60 * 1000 || at > now + BOOKED_AHEAD_MS)
      continue;
    for (const tableId of booking.table_ids) {
      if (!nextBooking.has(tableId)) nextBooking.set(tableId, booking.local_time);
    }
  }

  if (floor.isPending) return <Loading label="Loading the room" />;
  if (floor.isError) return <ErrorNotice message={floor.error.message} />;

  // Opens on the first room with tables in it: an empty room first in the list
  // told a waiter there were no tables while the dining room was full.
  const plan =
    floor.data.find((p) => p.id === planId) ??
    floor.data.find((p) => p.tables.length > 0) ??
    floor.data[0];
  if (!plan) {
    // A new restaurant lands here. This used to send it to the back office for
    // a floor plan, and nothing there could make one: a dead end on day one.
    return (
      <div className="pos__view">
        <div className="pos__view-body">
          <Empty
            title="No room set up yet"
            description={
              mayManage
                ? 'Name the room, then add its tables.'
                : 'Somebody who manages the floor has to set the room up first.'
            }
          />
          {mayManage && (
            <div className="room__start">
              <Button variant="primary" size="lg" onClick={() => setAddingRoom(true)}>
                Set up the room
              </Button>
            </div>
          )}
        </div>
        {addingRoom && (
          <NewRoomSheet
            locationId={locationId}
            first
            onDismiss={() => setAddingRoom(false)}
            onMade={(id) => {
              setPlanId(id);
              setAddingRoom(false);
              setEditing(true);
            }}
          />
        )}
      </div>
    );
  }

  const selected = plan.tables.find((t) => t.id === selectedId) ?? null;
  const toClear = plan.tables.filter((t) => t.state === 'dirty').length;
  const foodFor = foodUp;

  // Two tables pushed together are one table to everybody in the room, and a
  // merged-away table borrows its primary's `is_occupied` — so counting every
  // row counted one party twice and told a host the room was fuller than it
  // is. Units are the tables nothing was pushed into.
  const units = plan.tables.filter((t) => t.merged_into_id === null);
  const occupied = units.filter((t) => t.is_occupied).length;

  const describe = (table: FloorTable) =>
    `Table ${table.label}, ${describeTableState(table.state)}` +
    (table.merged_into_id !== null
      ? `, pushed into table ${labelOf(plan.tables, table.merged_into_id)}`
      : table.merged_table_ids.length > 0
        ? `, ${table.combined_seats} seats with ${table.merged_table_ids.length} more pushed in`
        : '') +
    (table.seated_minutes !== null ? `, seated ${formatSeatedFor(table.seated_minutes)}` : '') +
    (describeFoodUp(foodFor(table.order_id))
      ? `, ${describeFoodUp(foodFor(table.order_id))}`
      : '') +
    (nextBooking.has(table.id) ? `, booked for ${nextBooking.get(table.id)}` : '');

  const tile = (table: FloorTable) => (
    <button
      key={table.id}
      type="button"
      className={
        `room__table room__table--${tableTone(table)}` +
        (table.merged_into_id !== null ? ' room__table--joined' : '')
      }
      onClick={() => setSelectedId(table.id)}
      aria-label={describe(table)}
    >
      <span className="room__label">{table.label}</span>
      {/* Above the state word, because it outranks it: a table can be
          twenty minutes in and have food going cold at the pass. */}
      <FoodUpTag food={foodFor(table.order_id)} />
      {/* The state in words, always. Colour is the fast channel, not
          the only one. */}
      <span className="room__state">
        {table.merged_into_id !== null
          ? `With ${labelOf(plan.tables, table.merged_into_id)}`
          : describeTableState(table.state)}
      </span>
      {nextBooking.has(table.id) && (
        <span className="room__booked">Booked {nextBooking.get(table.id)}</span>
      )}
      <span className="room__foot">
        {/* `seats` is the furniture and `combined_seats` is what the
            unit seats — pushing tables together does not build
            chairs. A host asking "will six fit" needs the second,
            and a merged-away table seats nobody on its own. */}
        <span className="room__seats">
          {table.merged_into_id !== null
            ? `${table.seats} pushed in`
            : `${table.combined_seats} seats`}
        </span>
        {table.seated_minutes !== null && (
          <span className="room__timer tabular">{formatSeatedFor(table.seated_minutes)}</span>
        )}
      </span>
    </button>
  );

  // The map's table is a face, not the grid's card: the number, the state in
  // words, how long they have sat. Seats are the chairs drawn around it.
  const mapTile = (table: FloorTable, round: boolean) => (
    <button
      type="button"
      className={
        `rm__table room__table--${tableTone(table)}` +
        (round ? ' is-round' : '') +
        (table.merged_into_id !== null ? ' room__table--joined' : '')
      }
      onClick={() => setSelectedId(table.id)}
      aria-label={describe(table)}
    >
      <span className="room__label">{table.label}</span>
      <FoodUpTag food={foodFor(table.order_id)} />
      <span className="rm__state">
        {table.merged_into_id !== null
          ? `With ${labelOf(plan.tables, table.merged_into_id)}`
          : describeTableState(table.state)}
      </span>
      {table.seated_minutes !== null && (
        <span className="rm__timer">{formatSeatedFor(table.seated_minutes)}</span>
      )}
      {nextBooking.has(table.id) && (
        <span className="room__booked">Booked {nextBooking.get(table.id)}</span>
      )}
    </button>
  );

  return (
    <div className="pos__view">
      <div className="pos__view-head">
        <h1 className="pos__view-title">{plan.name}</h1>
        <p className="pos__view-sub">
          {units.length - occupied} free · {occupied} taken
          {toClear > 0 && ` · ${toClear} to clear`}
        </p>
        {/* Arranging the room is a manager's job; on everyone else's screen
            it was one more button to wonder about. */}
        {mayManage && (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Edit room
          </Button>
        )}
      </div>

      {/* One row for both switches: which room, and how to show it. Stacked,
          they were two bars of buttons before a single table. */}
      {(floor.data.length > 1 || (isArranged(plan.tables) && !arranging)) && (
        <div className="room__rooms room__switches">
          {/* More than one room — a terrace, upstairs — is a tab each. One
              room gets no switcher at all: the common restaurant has one. */}
          {floor.data.length > 1 && (
            <Segmented
              label="Rooms"
              scroll
              value={plan.id}
              onChange={setPlanId}
              options={floor.data.map((p) => ({ value: p.id, label: p.name }))}
            />
          )}
          {isArranged(plan.tables) && !arranging && (
            <Segmented
              label="Show the room as"
              value={view}
              onChange={setView}
              options={[
                { value: 'room', label: 'Map' },
                { value: 'tables', label: 'List' },
              ]}
            />
          )}
        </div>
      )}

      <div className="pos__view-body">
        {plan.tables.length === 0 ? (
          <Empty
            title="No tables yet"
            description={
              mayManage
                ? 'Tap Edit room to add the tables.'
                : 'Somebody who manages the floor has to add the tables first.'
            }
          />
        ) : arranging ? (
          <ArrangeRoom
            tables={plan.tables}
            start={laidOut}
            onDone={() => setArranging(false)}
          />
        ) : isArranged(plan.tables) && view === 'room' ? (
          <RoomMap tables={plan.tables} tile={mapTile} />
        ) : (
          <div className="room">{plan.tables.map((table) => tile(table))}</div>
        )}
      </div>

      {selected && (
        <TableSheet
          table={selected}
          tables={plan.tables}
          locationId={locationId}
          onOpenOrder={onOpenOrder}
          onDismiss={() => setSelectedId(null)}
        />
      )}

      {editing && (
        <EditRoomSheet
          locationId={locationId}
          planId={plan.id}
          planName={plan.name}
          onDismiss={() => setEditing(false)}
          onRemoved={() => {
            setEditing(false);
            setPlanId(null);
          }}
          onArrange={
            plan.tables.length > 0
              ? async () => {
                  setEditing(false);
                  setLaidOut(await layOut.layOut(plan.tables));
                  setArranging(true);
                }
              : undefined
          }
          onAddRoom={
            mayManage
              ? () => {
                  setEditing(false);
                  setAddingRoom(true);
                }
              : undefined
          }
        />
      )}

      {addingRoom && (
        <NewRoomSheet
          locationId={locationId}
          onDismiss={() => setAddingRoom(false)}
          onMade={(id) => {
            setPlanId(id);
            setAddingRoom(false);
            setEditing(true);
          }}
        />
      )}
    </div>
  );
}

function TableSheet({
  table,
  tables,
  locationId,
  onOpenOrder,
  onDismiss,
}: {
  table: FloorTable;
  tables: FloorTable[];
  locationId: string | undefined;
  onOpenOrder: (orderId: string) => void;
  onDismiss: () => void;
}) {
  const entitlements = useEntitlements();
  const seat = useSeatTable();
  const clear = useClearTable();
  const checkAsked = useCheckRequested();
  const release = useReleaseTable();
  const openOrder = useOpenOrder();
  const unmerge = useUnmergeTables();
  const [guests, setGuests] = useState('2');
  const [pushing, setPushing] = useState(false);
  const [movingSection, setMovingSection] = useState(false);
  const sections = useSections(table.floor_plan_id).data ?? [];
  // Which section a table is in is the floor's layout, set by whoever
  // manages it: the server refuses it to anybody else.
  const mayArrange = usePermission('floor.plan.manage');

  const error =
    seat.error ??
    clear.error ??
    checkAsked.error ??
    release.error ??
    openOrder.error ??
    unmerge.error;

  const joined = table.merged_table_ids.length > 0;
  const isJoinedAway = table.merged_into_id !== null;
  const mayPush = entitlements.has('floor.table_ops');
  const mayChangePlan = usePermission('billing.subscription.change');

  // One sheet at a time. A dialog inside a dialog gives the second one a scrim
  // that only darkens the first, which reads as a rendering fault — and on a
  // till a stray tap that lands past a half-covering scrim is a real cost.
  if (pushing) {
    return (
      <PushTogetherSheet table={table} tables={tables} onDismiss={() => setPushing(false)} />
    );
  }

  if (movingSection) {
    return (
      <EditTableSheet
        table={table}
        sections={sections}
        onDismiss={() => setMovingSection(false)}
      />
    );
  }

  function startTab() {
    if (!locationId) return;
    openOrder.mutate(
      {
        location_id: locationId,
        guest_count: table.guest_count ?? (Number(guests) || 2),
        table_id: table.id,
      },
      { onSuccess: (order) => onOpenOrder(order.id) },
    );
  }

  const hasTab = table.order_id !== null;

  // A table can be occupied with no tab for an entirely ordinary reason: a
  // party has been seated and nobody has taken the order yet. It is only
  // strange when the state says a tab exists and none is linked — which is a
  // real thing to see, because the event that ties the two together is
  // dispatched after the order's transaction and is allowed to fail quietly.
  const missingTab = !hasTab && table.is_occupied && table.state !== 'seated';

  // Built as a list rather than a run of conditionals so the sheet can tell
  // whether there is anything to do at all, and never hand the waiter an empty
  // grey bar where the buttons should be.
  const actions: ReactNode[] = [];

  if (hasTab) {
    actions.push(
      <Button
        key="open"
        variant="primary"
        size="lg"
        block
        onClick={() => onOpenOrder(table.order_id as string)}
      >
        Open the tab
      </Button>,
    );
  }

  // Always offered, from any state, because the floor accepts a new tab from
  // any state. A table that already has one is a six-top splitting or two
  // couples sharing; a table nobody cleared is a party who sat down anyway;
  // free is the busy case where the waiter skips seating entirely. None of
  // those make the table less in service, and the terminal should not be the
  // thing that refuses them.
  actions.push(
    <Button
      key="start"
      variant={hasTab || table.state === 'dirty' ? 'secondary' : 'primary'}
      size="lg"
      block
      loading={openOrder.isPending}
      onClick={startTab}
    >
      {hasTab ? 'Separate bill' : 'Start a tab'}
    </Button>,
  );

  if (table.can_seat) {
    actions.push(
      <Button
        key="seat"
        variant={table.state === 'free' ? 'secondary' : 'primary'}
        size="lg"
        block
        loading={seat.isPending}
        onClick={() =>
          seat.mutate(
            { tableId: table.id, guestCount: Number(guests) || 2 },
            { onSuccess: onDismiss },
          )
        }
      >
        Seat {guests}
      </Button>,
    );
  }

  if (table.state === 'dirty') {
    actions.push(
      <Button
        key="cleared"
        variant="primary"
        size="lg"
        block
        loading={clear.isPending}
        onClick={() => clear.mutate({ tableId: table.id }, { onSuccess: onDismiss })}
      >
        Cleared
      </Button>,
    );
  }

  // Layout rather than service, but it belongs on the table rather than in
  // the room editor: "this one is on the terrace tonight" is a thing somebody
  // says while looking at the table, and a room that rearranges between
  // services used to have to delete the table and add it again.
  // Its number, its seats, its section, or taking it away: the table's own
  // layout, for whoever manages the floor, in one sheet.
  if (mayArrange && table.merged_into_id === null) {
    actions.push(
      <Button key="edit" size="lg" block onClick={() => setMovingSection(true)}>
        Edit table
      </Button>,
    );
  }

  // The one state on the floor plan nobody could put a table into. The room
  // drew it and the state machine allowed it; there was no button, so a colour
  // the design spends specifically on "they have asked for the bill" never
  // appeared on a till. Offered while the table is occupied and before the
  // money starts moving — after that the table is paying, which the payment
  // itself records.
  if (table.state === 'ordered' || table.state === 'seated') {
    actions.push(
      <Button
        key="check"
        size="lg"
        block
        loading={checkAsked.isPending}
        onClick={() => checkAsked.mutate({ tableId: table.id }, { onSuccess: onDismiss })}
      >
        They have asked for the bill
      </Button>,
    );
  }

  // Pushing together is offered on the table that will hold the party, and
  // never on one already pushed into another — a run of tables comes apart in
  // one go, not a table at a time.
  if (mayPush && !isJoinedAway) {
    if (joined) {
      actions.push(
        <Button
          key="unmerge"
          size="lg"
          block
          loading={unmerge.isPending}
          onClick={() => unmerge.mutate({ tableId: table.id })}
        >
          Pull them apart
        </Button>,
      );
    } else if (candidatesFor(table, tables).length > 0) {
      actions.push(
        <Button key="merge" size="lg" block onClick={() => setPushing(true)}>
          Push tables together
        </Button>,
      );
    }
  }

  // Releasing a table with a live tab on it would strand the tab, so this is
  // offered for a party who sat and never ordered, and as the way out of a
  // table whose tab has gone missing.
  if (table.state === 'seated' || missingTab) {
    actions.push(
      <Button
        key="left"
        size="lg"
        block
        loading={release.isPending}
        onClick={() => release.mutate({ tableId: table.id }, { onSuccess: onDismiss })}
      >
        They left
      </Button>,
    );
  }

  return (
    <Sheet
      title={`Table ${table.label}`}
      onDismiss={onDismiss}
      actions={actions.length > 0 ? actions : null}
    >
      <p className="room__sheet-state">
        <span className={`room__dot room__dot--${tableTone(table)}`} aria-hidden="true" />
        {describeTableState(table.state)}
        {table.seated_minutes !== null && (
          <span className="room__sheet-since">
            {table.guest_count} guests · {formatSeatedFor(table.seated_minutes)}
          </span>
        )}
      </p>

      {isJoinedAway && (
        <p className="room__note">
          Pushed into table {labelOf(tables, table.merged_into_id as string)}. Use that table.
        </p>
      )}

      {joined && (
        <p className="room__note">
          {table.merged_table_ids.length === 1
            ? `With table ${labelOf(tables, table.merged_table_ids[0] as string)}`
            : `With tables ${table.merged_table_ids.map((id) => labelOf(tables, id)).join(', ')}`}{' '}
          · seats {table.combined_seats}
        </p>
      )}

      {/* An offer to upgrade is for whoever pays, not for whoever is on shift. */}
      {mayChangePlan && !mayPush && !isJoinedAway && !joined && (
        <div className="room__gate">
          <UpgradePrompt
            action="Pushing tables together"
            currentPlan={entitlements.plan}
            requiredPlan="professional"
            compact
          />
        </div>
      )}

      {missingTab && (
        <p className="room__orphan">
          No tab is linked to this table. Look for it under Orders, or tap They left.
        </p>
      )}

      {table.can_seat && (
        // Presets rather than a text field: a party size is one of seven
        // numbers, and none of them are worth opening a keyboard for.
        <div className="room__guests">
          <span className="room__guests-label">Guests</span>
          <Segmented
            label="Guests"
            value={guests}
            onChange={setGuests}
            options={GUEST_PRESETS.map((n) => ({ value: n, label: n }))}
          />
        </div>
      )}

      <ActionError error={error} action="This table action" />
    </Sheet>
  );
}

/** A table's label, for saying which one a party is really on. */
function labelOf(tables: FloorTable[], id: string): string {
  return tables.find((t) => t.id === id)?.label ?? 'another table';
}

/**
 * Which tables can be pushed into this one.
 *
 * A table with its own party is refused by the server, and rightly — moving
 * them is a different act with different consequences for turn time, and it
 * has its own control. So only tables with nobody at them and nothing on them
 * are offered, and a table already part of a run is not offered twice.
 */
function candidatesFor(primary: FloorTable, tables: FloorTable[]): FloorTable[] {
  return tables.filter(
    (t) =>
      t.id !== primary.id &&
      t.merged_into_id === null &&
      t.merged_table_ids.length === 0 &&
      !t.is_occupied &&
      t.order_id === null,
  );
}

/**
 * The answer to a party of six at the door when the room has two four-tops.
 *
 * Several at once rather than one at a time, because a run of tables is pushed
 * together in one go and taken apart in one go, and offering them singly would
 * mostly produce half-made units.
 */
function PushTogetherSheet({
  table,
  tables,
  onDismiss,
}: {
  table: FloorTable;
  tables: FloorTable[];
  onDismiss: () => void;
}) {
  const merge = useMergeTables();
  const [picked, setPicked] = useState<string[]>([]);

  const candidates = candidatesFor(table, tables);
  const seats =
    table.combined_seats +
    picked.reduce((sum, id) => sum + (tables.find((t) => t.id === id)?.seats ?? 0), 0);

  return (
    <Sheet
      title={`Push tables into ${table.label}`}
      onDismiss={onDismiss}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          disabled={picked.length === 0}
          loading={merge.isPending}
          onClick={() =>
            merge.mutate({ tableId: table.id, tableIds: picked }, { onSuccess: onDismiss })
          }
        >
          {picked.length === 0 ? 'Pick a table' : `Seat ${seats} on table ${table.label}`}
        </Button>
      }
    >
      <p className="room__note">
        The joined tables keep their labels and their place in the room, stop being seatable on
        their own, and start reading as part of {table.label}.
      </p>

      {candidates.length === 0 ? (
        <Empty
          title="Nothing free to push in"
          description="Only a table with nobody at it and nothing on it can be pushed in. Move a party with a transfer first — that is a different thing to do and it changes their turn time."
        />
      ) : (
        <div className="room__candidates">
          {candidates.map((candidate) => {
            const on = picked.includes(candidate.id);
            return (
              <button
                key={candidate.id}
                type="button"
                className={`room__candidate${on ? ' is-on' : ''}`}
                aria-pressed={on}
                onClick={() =>
                  setPicked((current) =>
                    on
                      ? current.filter((id) => id !== candidate.id)
                      : [...current, candidate.id],
                  )
                }
              >
                <span className="room__candidate-label">{candidate.label}</span>
                <span className="room__candidate-meta">
                  {candidate.seats} seats · {describeTableState(candidate.state)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Refusals name the table to press instead, and are written for
          somebody standing in front of a party. */}
      <ActionError error={merge.error} action="Pushing tables together" />
    </Sheet>
  );
}

/**
 * Which section a table is in, and who is on that section tonight.
 *
 * Both are the same conversation at a shift change — "you have the terrace,
 * and table nine is on it" — so they are one sheet rather than two places to
 * go. Who is on it is a manager's call and asks for names, so that half only
 * appears for somebody who can read the logins; the table half needs no such
 * thing.
 *
 * Taking the table out of every section is offered plainly. A room worked as
 * one has no sections in it, and getting back to that must not mean deleting
 * the table.
 */
function EditTableSheet({
  table,
  sections,
  onDismiss,
}: {
  table: FloorTable;
  sections: Section[];
  onDismiss: () => void;
}) {
  const update = useUpdateTable();
  const remove = useRemoveTable();
  const [label, setLabel] = useState(table.label);
  const [seats, setSeats] = useState(String(table.seats));
  const [removing, setRemoving] = useState(false);
  const changed = label.trim() !== table.label || Number(seats) !== table.seats;
  const setSection = useSetTableSection();
  const updateSection = useUpdateSection();
  const mayReadLogins = usePermission('identity.user.read');
  const logins = useLogins({ enabled: mayReadLogins });
  const current = sections.find((section) => section.id === table.section_id) ?? null;

  return (
    <Sheet
      title={`Table ${table.label}`}
      onDismiss={onDismiss}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          disabled={!changed || label.trim() === ''}
          loading={update.isPending}
          onClick={() =>
            update.mutate(
              { tableId: table.id, label: label.trim(), seats: Number(seats) },
              { onSuccess: onDismiss },
            )
          }
        >
          Save
        </Button>
      }
    >
      <div className="room__new-table">
        <Field label="Table">
          <Input value={label} onChange={(e) => setLabel(e.target.value)} />
        </Field>
        <div className="room__guests">
          <span className="room__guests-label">Seats</span>
          <Segmented
            label="Seats"
            value={(GUEST_PRESETS as readonly string[]).includes(seats) ? seats : ''}
            onChange={setSeats}
            options={GUEST_PRESETS.map((n) => ({ value: n, label: n }))}
          />
        </div>
      </div>
      <ActionError error={update.error} action="Changing the table" />

      {sections.length > 0 && (
        <div className="room__guests">
          <span className="room__guests-label">Section</span>
          <Segmented
            label="Section"
            scroll
            value={table.section_id ?? ''}
            onChange={(next) =>
              setSection.mutate({ tableId: table.id, sectionId: next === '' ? null : next })
            }
            options={[
              { value: '', label: 'None' },
              ...sections.map((section) => ({ value: section.id, label: section.name })),
            ]}
          />
        </div>
      )}

      <ActionError error={setSection.error} action="Moving the table" />

      {current !== null && mayReadLogins && (
        <section className="room__sections">
          <span className="room__guests-label">Who is on {current.name} tonight</span>
          <Segmented
            label="Server"
            scroll
            value={current.server_id ?? ''}
            onChange={(next) =>
              updateSection.mutate(
                next === ''
                  ? { sectionId: current.id, clearServer: true }
                  : { sectionId: current.id, serverId: next },
              )
            }
            options={[
              // Explicit, and the state every section ends the night in.
              { value: '', label: 'Nobody' },
              ...(logins.data ?? [])
                .filter((login) => login.is_active)
                .map((login) => ({ value: login.id, label: login.name })),
            ]}
          />
          <p className="room__section-note">
            Cleared at the end of a service rather than left on whoever worked it last.
          </p>
          <ActionError error={updateSection.error} action="Changing who is on the section" />
        </section>
      )}

      {/* Asked twice, as a room is. Refused while anybody is sitting there. */}
      {removing ? (
        <div
          className="room__remove"
          ref={(node) => node?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })}
        >
          <p className="room__remove-ask">Remove table {table.label}? Past orders keep it.</p>
          <div className="room__remove-acts">
            <Button variant="secondary" onClick={() => setRemoving(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => remove.mutate(table.id, { onSuccess: onDismiss })}
            >
              Remove table
            </Button>
          </div>
          <ActionError error={remove.error} action="Removing the table" />
        </div>
      ) : (
        <button
          type="button"
          className="room__add-room room__add-room--danger"
          onClick={() => setRemoving(true)}
        >
          Remove this table
        </button>
      )}
    </Sheet>
  );
}

/**
 * Adding a table.
 *
 * Setup, not service — so it is behind a deliberate tap rather than sitting
 * under the room where a thumb finds it mid-shift.
 */
function NewRoomSheet({
  locationId,
  first = false,
  onDismiss,
  onMade,
}: {
  locationId: string | undefined;
  first?: boolean;
  onDismiss: () => void;
  onMade: (planId: string) => void;
}) {
  const createPlan = useCreateFloorPlan();
  const [name, setName] = useState(first ? 'Dining room' : '');

  return (
    <Sheet
      title={first ? 'Set up the room' : 'Add a room'}
      onDismiss={onDismiss}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          loading={createPlan.isPending}
          disabled={name.trim() === '' || !locationId}
          onClick={() =>
            locationId &&
            createPlan.mutate(
              { location_id: locationId, name: name.trim() },
              { onSuccess: (plan) => onMade(plan.id) },
            )
          }
        >
          {first ? 'Next: add tables' : 'Add room'}
        </Button>
      }
    >
      <Field
        label="Room"
        hint={
          first
            ? 'What the staff call it. More rooms — a terrace, upstairs — can be added later.'
            : 'A terrace, upstairs, the bar. Each gets its own tab above the tables.'
        }
      >
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          autoFocus
        />
      </Field>
      <ActionError
        error={createPlan.error}
        action="Another room"
        title="That room was not added"
      />
    </Sheet>
  );
}

function EditRoomSheet({
  locationId,
  planId,
  planName,
  onDismiss,
  onRemoved,
  onAddRoom,
  onArrange,
}: {
  locationId: string | undefined;
  planId: string;
  planName: string;
  onDismiss: () => void;
  onRemoved: () => void;
  onAddRoom?: (() => void) | undefined;
  onArrange?: (() => void) | undefined;
}) {
  const createTable = useCreateTable();
  const sections = useSections(planId);
  const createSection = useCreateSection();
  const [label, setLabel] = useState('');
  const [seats, setSeats] = useState('2');
  const [sectionId, setSectionId] = useState('');
  const [newSection, setNewSection] = useState('');
  const removeRoom = useRemoveFloorPlan();
  const [removing, setRemoving] = useState(false);

  function submit() {
    if (!label.trim() || !locationId) return;
    createTable.mutate(
      {
        floor_plan_id: planId,
        label: label.trim(),
        seats: Number(seats) || 2,
        // A table belongs to a section or to none. Which section it is in can
        // only be said as it is added — there is no route to move one
        // afterwards, so the form does not pretend otherwise.
        ...(sectionId === '' ? {} : { section_id: sectionId }),
      },
      { onSuccess: () => setLabel('') },
    );
  }

  return (
    <Sheet
      title="Edit room"
      onDismiss={onDismiss}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          loading={createTable.isPending}
          disabled={label.trim() === ''}
          onClick={submit}
        >
          Add table
        </Button>
      }
    >
      {/* The table and its seats on one row: they are one thing being added. */}
      <div className="room__new-table">
        <Field label="Table">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="12, B4, Window"
          />
        </Field>

        <div className="room__guests">
          <span className="room__guests-label">Seats</span>
          <Segmented
            label="Seats"
            value={seats}
            onChange={setSeats}
            options={GUEST_PRESETS.map((n) => ({ value: n, label: n }))}
          />
        </div>
      </div>

      {/* A section is a group of tables one server looks after. Optional, and
          most rooms never use one — so it sits under the table itself rather
          than asking a question a single-room restaurant does not have. */}
      <section className="room__sections">
        <span className="room__guests-label">Section</span>
        {(sections.data ?? []).length > 0 && (
          <Segmented
            label="Section"
            value={sectionId}
            onChange={setSectionId}
            options={[
              { value: '', label: 'None' },
              ...(sections.data ?? []).map((section) => ({
                value: section.id,
                label: section.name,
              })),
            ]}
          />
        )}

        <div className="room__section-add">
          <Input
            value={newSection}
            onChange={(e) => setNewSection(e.target.value)}
            placeholder="New section, e.g. Terrace"
            aria-label="New section name"
          />
          <Button
            loading={createSection.isPending}
            disabled={newSection.trim() === ''}
            onClick={() =>
              createSection.mutate(
                { floorPlanId: planId, name: newSection.trim() },
                {
                  onSuccess: (made) => {
                    setNewSection('');
                    setSectionId(made.id);
                  },
                },
              )
            }
          >
            Add section
          </Button>
        </div>
        {createSection.isError && (
          <ErrorNotice
            title="That section was not added"
            message={createSection.error.message}
          />
        )}
      </section>

      {createTable.isError && (
        <ErrorNotice title="That table was not added" message={createTable.error.message} />
      )}

      {/* The room's own actions, on one line rather than a stack of three. */}
      <div className="room__links">
        {onArrange && (
          <button type="button" className="room__add-room" onClick={onArrange}>
            Arrange tables
          </button>
        )}
        {onAddRoom && (
          <button type="button" className="room__add-room" onClick={onAddRoom}>
            Add another room
          </button>
        )}
        {!removing && (
          <button
            type="button"
            className="room__add-room room__add-room--danger"
            onClick={() => setRemoving(true)}
          >
            Remove this room
          </button>
        )}
      </div>

      {/* Asked twice, because it takes every table in the room with it. */}
      {removing ? (
        <div
          className="room__remove"
          // Opened at the foot of a sheet that scrolls: brought into view, so
          // the question is not asked half off the screen.
          ref={(node) => node?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })}
        >
          <p className="room__remove-ask">
            Remove {planName} and all its tables? Past orders keep them.
          </p>
          <div className="room__remove-acts">
            <Button variant="secondary" onClick={() => setRemoving(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={removeRoom.isPending}
              onClick={() => removeRoom.mutate(planId, { onSuccess: onRemoved })}
            >
              Remove room
            </Button>
          </div>
          {removeRoom.isError && (
            <ErrorNotice title="The room was not removed" message={removeRoom.error.message} />
          )}
        </div>
      ) : null}
    </Sheet>
  );
}
